import { useEffect } from 'react'
import { useOptionalTenant } from '@/contexts/TenantContext'
import { isAllServicesStorePath } from '@/lib/namedRoutes'

/**
 * `meta[theme-color]` takes a colour, not a custom property, so a `var(--x)`
 * override has to be resolved against the document before it is written.
 */
function resolveColor(color: string): string {
    const token = /^var\(\s*(--[\w-]+)\s*\)$/.exec(color.trim())
    if (!token) return color

    const value = getComputedStyle(document.documentElement).getPropertyValue(token[1]).trim()
    return value || color
}

export function useThemeColor(overrideActive: boolean = false, overrideColor: string = '#2D1AFF') {
    const tenantThemeColor = useOptionalTenant()?.themeColor

    useEffect(() => {
        // Ensure meta tag exists
        const meta =
            document.querySelector<HTMLMetaElement>('meta[name="theme-color"]') ||
            (() => {
                const m = document.createElement('meta')
                m.setAttribute('name', 'theme-color')
                document.head.prepend(m)
                return m as HTMLMetaElement
            })()

        // Function to apply the correct color
        function updateColor() {
            if (overrideActive) {
                meta.content = resolveColor(overrideColor)
            } else if (tenantThemeColor) {
                meta.content = tenantThemeColor
            } else {
                const path = location.pathname
                meta.content =
                    (path.startsWith('/store') && !path.startsWith('/store/search') && !isAllServicesStorePath(path)) ||
                    path === '/welcome' ||
                    path.startsWith('/account')
                        ? '#2D1AFF'
                        : '#F5F1ED'
            }
        }

        // Wrap history methods to dispatch a custom event
        const wrap = (orig: typeof history.pushState) =>
            function (this: History, ...args: Parameters<typeof history.pushState>) {
                orig.apply(this, args)
                window.dispatchEvent(new Event('locationchange'))
            }
        history.pushState = wrap(history.pushState)
        history.replaceState = wrap(history.replaceState)

        // Listen for navigation events
        ;['popstate', 'locationchange'].forEach((evt) => window.addEventListener(evt, updateColor))

        // Initialize on mount
        updateColor()

        // Cleanup listeners on unmount
        return () => {
            ;['popstate', 'locationchange'].forEach((evt) => window.removeEventListener(evt, updateColor))
        }
    }, [overrideActive, overrideColor, tenantThemeColor])
}
